import { useEffect, useState, useRef } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { getAuth, signOut } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { updateGlobalProfile, readGlobalProfiles } from '@services/firebaseGlobal';
import { readUserCollection, uploadImageToStorage, writeUserDocument } from '@services/firebaseUser';
import { FaUser, FaChartBar, FaHeadset, FaEdit, FaPhotoVideo, FaStore, FaCarAlt, FaShoppingBag, FaArchive, FaCog, FaShareAlt } from 'react-icons/fa';
import { TbAutomation } from 'react-icons/tb';
import { getFirebase } from '@services/firebase';
import { useAlert } from '../contexts/ToastContext';

const { auth } = getFirebase();
const Profile = ({ userRole: propUserRole }) => {
  const [user, loading, error] = useAuthState(auth);
  const navigate = useNavigate();
  const showAlert = useAlert();
  const [userRole, setUserRole] = useState(() => {
    // Use prop if available, otherwise try localStorage
    if (propUserRole) return propUserRole;
    try {
      const cachedRole = localStorage.getItem('userRole');
      return cachedRole || '';
    } catch (err) {
      console.error('Error reading userRole from localStorage:', err);
      return '';
    }
  });
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    profilePicture: '',
  });
  const [imageFile, setImageFile] = useState(null);
  const [uploadError, setUploadError] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isProfileLoading, setIsProfileLoading] = useState(true);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchUserData = async () => {
      if (user) {
        try {
          setIsProfileLoading(true);
          // Fetch user role from cUsers
          const userList = await readGlobalProfiles('cUsers');
          const currentUserData = userList.find(userData => userData.id === user.uid);
          if (currentUserData) {
            let role = 'User';
            if (currentUserData.isadminuser) {
              role = 'Admin';
            } else if (currentUserData.issuperuser) {
              role = 'Super';
            } else if (currentUserData.isbuyer) {
              role = 'Buyer';
            } else if (currentUserData.ismover) {
              role = 'Mover';
            }
            setUserRole(role);
            // Sync role to localStorage
            try {
              localStorage.setItem('userRole', role);
            } catch (err) {
              console.error('Error saving userRole to localStorage:', err);
            }
          }

          // Fetch profile data from /profile/<uid>
          const profileDataList = await readUserCollection('profile', user.uid);
          const profile = profileDataList.length > 0 ? profileDataList[0] : null;
          if (profile) {
            setProfileData({
              firstName: profile.f_name || '',
              lastName: profile.l_name || '',
              profilePicture: profile.p_picture || '',
            });
          }
        } catch (err) {
          console.error('Error fetching user data:', err);
        } finally {
          setIsProfileLoading(false);
        }
      }
    };

    fetchUserData();
  }, [user]);

  const handleSignOut = async () => {
    try {
      if (user) {
        const userData = { isactive: false, securityCode: null };
        await updateGlobalProfile('cUsers', user.uid, userData);
      }
      await signOut(auth);
      localStorage.clear();
      sessionStorage.clear();
      document.cookie.split(';').forEach((c) => {
        document.cookie = c
          .replace(/^ +/, '')
          .replace(/=.*/, '=;expires=' + new Date().toUTCString() + ';path=/');
      });
      window.location.href = '/login';
    } catch (err) {
      console.error('Error signing out:', err.message, err.stack);
      showAlert('Failed to sign out: ' + err.message);
    }
  };

  // Handle image upload
  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // For preview, create a local URL
      const previewUrl = URL.createObjectURL(file);
      const originalPicture = profileData.profilePicture;
      setImageFile(file);
      setProfileData(prev => ({
        ...prev,
        profilePicture: previewUrl,
      }));
      setIsUploading(true);
      setUploadError('');

      try {
        const downloadURL = await uploadImageToStorage(user.uid, file);
        setProfileData(prev => ({
          ...prev,
          profilePicture: downloadURL,
        }));

        // Save the download URL to the profile table at /profile/<uid>
        const updatedProfileData = {
          f_name: profileData.firstName,
          l_name: profileData.lastName,
          p_picture: downloadURL,
        };
        await writeUserDocument('profile', user.uid, updatedProfileData, user.uid);

        // Verify the update by fetching the updated profile document
        const profileDataList = await readUserCollection('profile', user.uid);
        const updatedProfile = profileDataList.length > 0 ? profileDataList[0] : null;
        if (updatedProfile && updatedProfile.p_picture === downloadURL) {
        } else {
          throw new Error('Profile picture URL not updated in profile table.');
        }

        setUploadError('');
      } catch (err) {
        console.error('Error saving profile picture:', err);
        setUploadError('Failed to save profile picture: ' + err.message);
        // Revert to the original profile picture if the upload fails
        setProfileData(prev => ({
          ...prev,
          profilePicture: originalPicture,
        }));
        setImageFile(null);
      } finally {
        setIsUploading(false);
      }
    }
  };

  // Trigger file input click
  const handleEditAvatar = () => {
    fileInputRef.current.click();
  };

  // Handle image load completion
  const handleImageLoad = () => {
    setIsImageLoaded(true);
  };

  useEffect(() => {
    if (!loading && !user && !error) {
      navigate('/login', { replace: true });
    }
  }, [user, loading, error, navigate]);

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error.message}</div>;

  return (
    <>
      <style>
        {`
          /* Reset and Base Styles */
          body {
            margin: 0;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
            background-color: #fbfafa;
          }

          /* Profile Page Specific Styles */
          .profile-page {
            display: flex;
            flex-direction: column;
            padding: 10px;
            padding-bottom: 50px;
            background-color: #f5f5f5;
            min-height: 100vh;
            overflow: visible;
          }

          .profile-header {
            display: flex;
            flex-direction: column;
            align-items: center;
            margin-bottom: 10px;
            padding: 8px;
            background: #f5f5f5;
            position: relative;
          }

          .avatar {
            background: #fa4848;
            border-radius: 50%;
            width: 70px;
            height: 70px;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-bottom: 8px;
            position: relative;
          }

          .avatar svg {
            color: white;
            font-size: 24px;
          }

          .avatar img {
            width: 100%;
            height: 100%;
            border-radius: 50%;
            object-fit: cover;
            opacity: 0;
            transition: opacity 0.2s;
          }

          .avatar img.loaded {
            opacity: 1;
          }

          /* Edit Button: Faded Background and Pen on Hover */
          .avatar-edit-btn {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: transparent;
            color: #ffffff;
            border: none;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            font-size: 16px;
            opacity: 0;
            transition: opacity 0.2s;
            text-shadow: 0 0 2px rgba(0, 0, 0, 0.5);
          }

          .avatar:hover .avatar-edit-btn {
            opacity: 1;
            background: rgba(0, 0, 0, 0.5);
          }

          .avatar-loading {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(255, 255, 255, 0.7);
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 50%;
          }

          .avatar-loading::after {
            content: '';
            width: 20px;
            height: 20px;
            border: 2px solid #fa4848;
            border-top: 2px solid transparent;
            border-radius: 50%;
            animation: spin 1s linear infinite;
          }

          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }

          .user-info {
            text-align: center;
            margin-bottom: 15px;
          }

          .user-name {
            font-size: 12px;
            font-weight: bold;
            color: #333;
            margin: 0;
          }

          .profile-nav {
            display: flex;
            flex-direction: column;
            margin-bottom: 10px;
          }

          .nav-card {
            background: #ffffff;
            border-radius: 12px;
            margin-bottom: 8px;
            padding: 10px;
            box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
            overflow: hidden;
          }

          .nav-item {
            display: flex;
            align-items: center;
            padding: 6px 12px;
            color: #333;
            font-size: 14px;
            font-weight: 500;
            border-radius: 5px;
            cursor: pointer;
          }

          .nav-icon {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 20px;
            height: 20px;
            border-radius: 50%;
            margin-right: 8px;
          }

          .user-icon {
            background: #333;
            color: white;
          }

          .job-wizard-icon,
          .analytics-icon,
          .support-icon {
            background: #666;
            color: white;
          }

          .nav-link {
            display: flex;
            align-items: center;
            padding: 6px 12px;
            color: #333;
            font-size: 14px;
            font-weight: 500;
            border-radius: 5px;
            cursor: pointer;
          }

          /* Forms and Inputs */
          .error {
            color: #fa4848;
            margin-bottom: 6px;
            font-size: 12px;
            text-align: center;
          }

          /* Adjust size for smaller screens */
          @media (max-width: 450px) {
            .profile-page {
              padding: 8px;
              padding-bottom: 50px;
            }

            .profile-header {
              padding: 6px;
              margin-bottom: 8px;
            }

            .avatar {
              width: 60px;
              height: 60px;
            }

            .avatar svg {
              font-size: 40px;
            }

            .avatar-edit-btn {
              font-size: 14px;
            }

            .user-name {
              font-size: 12px;
            }

            .nav-card {
              padding: 8px;
              margin-bottom: 6px;
            }

            .nav-item,
            .nav-link {
              padding: 5px 10px;
              font-size: 12px;
            }

            .nav-icon {
              width: 18px;
              height: 18px;
              margin-right: 6px;
            }

            .nav-icon svg {
              font-size: 14px;
            }

            .error {
              font-size: 10px;
            }
          }
        `}
      </style>

      <div className="profile-page">
        {uploadError && <div className="error">{uploadError}</div>}
        <div className="profile-header">
          <div className="avatar">
            {isProfileLoading ? (
              <div className="avatar-loading"></div>
            ) : profileData.profilePicture ? (
              <img
                src={profileData.profilePicture}
                alt="Profile"
                className={isImageLoaded ? 'loaded' : ''}
                onLoad={handleImageLoad}
              />
            ) : (
              <FaUser />
            )}
            {isUploading && <div className="avatar-loading"></div>}
            <button className="avatar-edit-btn" onClick={handleEditAvatar} disabled={isUploading}>
              <FaEdit />
            </button>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              accept="image/*"
              onChange={handleImageChange}
            />
          </div>
          <div className="user-info">
            <p className="user-name">{profileData.firstName} {profileData.lastName}</p>
          </div>
        </div>
        <div className="profile-nav">
          {/* Card 1: User Admin + App Share (Admin/Super) | User Profile (others) */}
          <div className="nav-card">
            {(userRole === 'Admin' || userRole === 'Super') ? (
              <>
                <div onClick={() => navigate('/user-admin')} className="nav-item">
                  <span className="nav-icon user-icon"><FaUser size={14} /></span>
                  User Admin
                </div>
                <div onClick={() => navigate('/app-share')} className="nav-item">
                  <span className="nav-icon support-icon"><FaShareAlt size={14} /></span>
                  App Share
                </div>
              </>
            ) : (
              <div onClick={() => navigate('/user-profile')} className="nav-item">
                <span className="nav-icon user-icon"><FaUser size={14} /></span>
                User Profile
              </div>
            )}
            {userRole === 'Buyer' && (
              <div onClick={() => navigate('/order-history')} className="nav-item">
                <span className="nav-icon job-wizard-icon"><FaShoppingBag size={14} /></span>
                Order History
              </div>
            )}
          </div>
          {/* Card 2: Meta Data + Job Wizard + House Keeping (Admin) | Photo Studio + Analytics (others) */}
          {userRole !== 'User' && userRole !== 'Mover' && (
            <div className="nav-card">
              {userRole === 'Admin' ? (
                <>
                  <div onClick={() => navigate('/meta')} className="nav-item">
                    <span className="nav-icon support-icon"><TbAutomation size={14} /></span>
                    Meta Data
                  </div>
                  <div onClick={() => navigate('/job-wizard')} className="nav-item">
                    <span className="nav-icon job-wizard-icon"><FaArchive size={14} /></span>
                    Job Wizard
                  </div>
                  <div onClick={() => navigate('/housekeeping')} className="nav-item">
                    <span className="nav-icon job-wizard-icon"><FaEdit size={14} /></span>
                    House Keeping
                  </div>
                </>
              ) : (userRole === 'Buyer' || userRole === 'Super') ? (
                <>
                  <div onClick={() => navigate('/imagestudio')} className="nav-item">
                    <span className="nav-icon support-icon"><FaPhotoVideo size={14} /></span>
                    Photo Studio
                  </div>
                  <div onClick={() => navigate('/analytics')} className="nav-item">
                    <span className="nav-icon analytics-icon"><FaChartBar size={14} /></span>
                    Analytics
                  </div>
                </>
              ) : null}
            </div>
          )}
          {/* Card 3: Inventory (+ Meta Data before for Super, + Photo Studio for Admin) */}
          {(userRole === 'Admin' || userRole === 'Super') ? (
            <div className="nav-card">
              {userRole === 'Super' && (
                <div onClick={() => navigate('/meta')} className="nav-item">
                  <span className="nav-icon support-icon"><TbAutomation size={14} /></span>
                  Meta Data
                </div>
              )}
              <div onClick={() => navigate('/')} className="nav-item">
                <span className="nav-icon support-icon"><FaStore size={14} /></span>
                Inventory
              </div>
              {userRole === 'Admin' && (
                <div onClick={() => navigate('/imagestudio')} className="nav-item">
                  <span className="nav-icon support-icon"><FaPhotoVideo size={14} /></span>
                  Photo Studio
                </div>
              )}
            </div> 
          ) : (
            <div className="nav-card">
              <div onClick={() => navigate('/user-chat', { state: { from: 'support' } })} className="nav-item">
                <span className="nav-icon support-icon"><FaHeadset size={14} /></span>
                Support
              </div>
            </div>
          )}
          {/* Settings + Sign Out Card */}
          <div className="nav-card">
            <div onClick={() => navigate('/settings')} className="nav-item">
              <span className="nav-icon support-icon"><FaCog size={14} /></span>
              Settings
            </div>
            <div onClick={handleSignOut} className="nav-item nav-link">
              <span className="nav-icon user-icon"><FaUser size={14} /></span>
              Sign Out
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Profile;